import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { PaginatorModule, type PaginatorState } from 'primeng/paginator';
import { EllipsisTooltipDirective } from './ellipsis-tooltip.directive';
import {
  DELEGATE_STATUS_FILTER_OPTIONS,
  displayNameFromEmail,
  grantedOwnerScopes,
  ownerScopeStates,
  isConsumerOwnerRole,
  isPlatformAdminOwnerRole,
  ownerRoleDisplayLabel,
  normalizeScopeKey,
  rowScopes,
  scopesForOwnerRole,
  descriptionForScope,
  groupScopesByDelegationRole,
  isBundledCompanionScope,
  scopeDisplayLabel,
  withBundledDelegationScopes,
  type AddDelegateForm,
  type AddDelegateFormErrors,
  type DelegateScopeOption,
  type DelegateStatus,
  type ManageDelegateRow,
  type ManageDelegatesDateRange,
  type ManageDelegatesFilters,
  type ManageDelegatesTab,
  type ManageOwnerRow,
  type OwnerScopeState,
  type RequestOwnerPayload,
  type RequestScopeItem,
} from './delegate.models';
import {
  DateRangePickerComponent,
  type DateRange,
} from './date-range-picker/date-range-picker.component';
import {
  DelegateApiService,
  RolesApiService,
  istToUtc,
  utcToIst,
  toDelegationRole,
  type CreateDelegationRole,
  type DeleteDelegationConstraintsPayload,
  type DelegatorDelegationItem,
  type DelegateDelegationItem,
  type DelegationListQuery,
  type DelegationRole,
} from '@angular-workspace/services';

const DEFAULT_ASSET_BASE = '/icons/delegate';

const ASSUME_DELEGATE_STORAGE_KEY = 'mahaagx.assumeDelegateRole';

function readAssumeDelegateRole(): boolean {
  try {
    const stored = sessionStorage.getItem(ASSUME_DELEGATE_STORAGE_KEY);
    if (stored === '0') return false;
    if (stored === '1') return true;
  } catch {
    /* ignore unavailable storage */
  }
  return false;
}

let assumeDelegateRoleValue = readAssumeDelegateRole();

/** Current delegate-session flag. Prefer setAssumeDelegateRole from the workspace switcher. */
export function getAssumeDelegateRole(): boolean {
  return assumeDelegateRoleValue;
}

/** Current Workspace → false. Any Delegated Access owner → true. */
export function setAssumeDelegateRole(value: boolean): void {
  assumeDelegateRoleValue = value;
  try {
    sessionStorage.setItem(ASSUME_DELEGATE_STORAGE_KEY, value ? '1' : '0');
  } catch {
    /* ignore unavailable storage */
  }
}

/** @deprecated Use getAssumeDelegateRole() — kept so existing imports compile. */
export const ASSUME_DELEGATE_ROLE = assumeDelegateRoleValue;

/** Scope chip / +N popup: hide catalog placeholders that are still pending. */
const VISIBLE_SCOPE_STATUSES = new Set<string>([
  'grant',
  'granted',
  'active',
  'rejected',
  'deleted',
  'revoked',
]);

/** Prefix for per-role tab persistence (cleared on logout). */
export const MANAGE_DELEGATES_TAB_STORAGE_PREFIX =
  'mahaagx.manage-delegates.active-tab';

function tabStorageKey(scope: string): string {
  const safe = (scope || 'default').trim() || 'default';
  return `${MANAGE_DELEGATES_TAB_STORAGE_PREFIX}.${safe}`;
}

function persistTab(scope: string, tab: ManageDelegatesTab): void {
  try {
    sessionStorage.setItem(tabStorageKey(scope), tab);
  } catch {
    /* ignore unavailable storage */
  }
}

/** Clears all manage-delegates tab keys (call on logout). */
export function clearManageDelegatesTabStorage(): void {
  try {
    const keys: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (
        key &&
        (key === MANAGE_DELEGATES_TAB_STORAGE_PREFIX ||
          key.startsWith(`${MANAGE_DELEGATES_TAB_STORAGE_PREFIX}.`))
      ) {
        keys.push(key);
      }
    }
    // Legacy unscoped key from earlier builds
    keys.push('mahaagx.manage-delegates.active-tab');
    for (const key of keys) {
      sessionStorage.removeItem(key);
    }
  } catch {
    /* ignore */
  }
}

@Component({
  selector: 'lib-manage-delegates',
  standalone: true,
  imports: [
    FormsModule,
    PaginatorModule,
    DateRangePickerComponent,
    EllipsisTooltipDirective,
  ],
  templateUrl: './manage-delegates.component.html',
  styleUrl: './manage-delegates.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    '(document:click)': 'onDocumentClick($event)',
  },
})
export class ManageDelegatesComponent implements OnInit, OnDestroy {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly delegateApi = inject(DelegateApiService);
  private readonly rolesApi = inject(RolesApiService);

  /** Base URL for icons (plus, filter, chevron, leaf, calendar, close). */
  readonly assetBase = input(DEFAULT_ASSET_BASE);

  /**
   * Isolates tab persistence per role/panel (e.g. consumer | provider | org-admin)
   * so switching roles does not restore another role’s Owners tab.
   */
  readonly tabStorageScope = input('default');

  /**
   * Delegate-facing view: hide My Delegates / Add Delegate and show only the
   * owners who granted this user access (name, email, scopes).
   */
  readonly ownersOnly = input(false);

  /**
   * Delegated Access: id of the owner workspace currently in use (`null` in My Workspace).
   * Switch is hidden for that owner.
   */
  readonly activeOwnerId = input<string | null>(null);

  /** Live delegate-session flag from the workspace switcher. */
  get isDelegateRole(): boolean {
    return getAssumeDelegateRole();
  }

  /** When true, shows the Add Delegate dialog. */
  readonly enableAddModal = input(true);

  /** Scope options for the Add Delegate dropdown. */
  readonly scopeOptions = input<DelegateScopeOption[]>([]);

  /** Parent-supplied rows. When empty, the component uses the rows it loaded from the API. */
  readonly delegates = input<ManageDelegateRow[]>([]);
  readonly owners = input<ManageOwnerRow[]>([]);
  readonly loading = input(false);

  readonly addDelegate = output<AddDelegateForm>();
  readonly deleteDelegate = output<ManageDelegateRow>();
  readonly withdrawOwner = output<ManageOwnerRow>();
  readonly switchOwner = output<ManageOwnerRow>();
  readonly requestOwner = output<RequestOwnerPayload>();
  readonly tabChange = output<ManageDelegatesTab>();
  readonly filtersChange = output<ManageDelegatesFilters>();

  readonly activeTab = signal<ManageDelegatesTab>('delegates');
  readonly filtersOpen = signal(false);
  readonly showAddModal = signal(false);
  readonly showRequestModal = signal(false);
  readonly requestTarget = signal<ManageOwnerRow | null>(null);
  selectedRequestScopes: string[] = [];
  readonly showDowngradeModal = signal(false);
  readonly downgradeTarget = signal<ManageDelegateRow | null>(null);
  readonly downgradeSubmitting = signal(false);
  selectedDowngradeScopes: string[] = [];
  downgradeError = '';
  readonly showUpgradeModal = signal(false);
  readonly upgradeTarget = signal<ManageDelegateRow | null>(null);
  readonly upgradeSubmitting = signal(false);
  selectedUpgradeScopes: string[] = [];
  upgradeError = '';
  readonly showConfirmModal = signal(false);
  readonly confirmMode = signal<'delete' | 'revoke'>('revoke');
  readonly confirmTarget = signal<ManageDelegateRow | ManageOwnerRow | null>(
    null,
  );
  readonly openActionId = signal<string | null>(null);
  readonly openScopesRowId = signal<string | null>(null);
  readonly statusDropdownOpen = signal(false);
  readonly scopeDropdownOpen = signal(false);
  readonly dateRangeOpen = signal(false);
  readonly addSubmitting = signal(false);
  readonly confirmSubmitting = signal(false);
  readonly listLoading = signal(false);

  /** Rows from GET /auth/delegation/delegator (empty until first load). */
  private readonly localDelegates = signal<ManageDelegateRow[]>([]);
  /** Rows from GET /auth/delegation/delegate (empty until first load). */
  private readonly localOwners = signal<ManageOwnerRow[]>([]);

  readonly statusOptions = DELEGATE_STATUS_FILTER_OPTIONS;

  get filterStatusOptions(): typeof DELEGATE_STATUS_FILTER_OPTIONS {
    return this.statusOptions.filter((opt) => opt.value !== 'rejected');
  }

  filterDateRangeValue: DateRange | null = null;
  expiryDateRangeValue: DateRange | null = null;
  /** Expiry cannot be in the past — same pattern as other MahaAgX single-date pickers. */
  readonly expiryMinDate: Date = (() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  })();
  selectedStatuses: Exclude<DelegateStatus, 'all'>[] = [];
  readonly pageSize = 10;
  currentPage = 1;
  listTotal = 0;
  sortColumn: 'createdAt' | 'expiryAt' | null = null;
  sortDirection: 'asc' | 'desc' = 'asc';
  private listRequestSeq = 0;

  readonly commentsMaxLength = 200;

  addForm: AddDelegateForm = {
    email: '',
    scope: '',
    expiryAt: '',
    comments: '',
  };
  selectedAddScopes: string[] = [];
  scopeSearchQuery = '';
  highlightedScopeIndex = 0;
  addFormErrors: AddDelegateFormErrors = {};
  dropdownPosition = { top: 0, left: 0 };
  scopesPopupPosition = { top: 0, left: 0 };
  scopeTooltipPosition = { top: 0, left: 0, above: true };
  scopeMenuPosition = { top: 0, left: 0, width: 0, maxHeight: 280 };
  readonly openActionRow = signal<ManageDelegateRow | ManageOwnerRow | null>(
    null,
  );
  readonly openScopesRow = signal<ManageDelegateRow | ManageOwnerRow | null>(
    null,
  );
  readonly scopeTooltip = signal<{ label: string; text: string } | null>(null);
  private actionAnchor: HTMLElement | null = null;
  private scopesAnchor: HTMLElement | null = null;
  private scopeTriggerEl: HTMLElement | null = null;
  private readonly onViewportChange = (): void => {
    this.syncActionDropdownPosition();
    this.syncScopesPopupPosition();
    this.syncScopeMenuPosition();
    this.hideScopeTooltip();
  };

  /** Capture phase so modal/date-picker stopPropagation cannot keep Choose scope open. */
  private readonly onCaptureClick = (event: MouseEvent): void => {
    if (!this.scopeDropdownOpen()) {
      return;
    }
    const target = event.target as HTMLElement | null;
    if (this.isScopePickerTarget(target)) {
      return;
    }
    this.closeScopeDropdown();
    this.cdr.markForCheck();
  };

  ngOnInit(): void {
    window.addEventListener('scroll', this.onViewportChange, true);
    window.addEventListener('resize', this.onViewportChange);
    document.addEventListener('click', this.onCaptureClick, true);
    if (this.ownersOnly()) {
      this.activeTab.set('owners');
      void this.loadOwners();
      return;
    }
    this.activeTab.set('delegates');
    void this.loadDelegates();
  }

  ngOnDestroy(): void {
    window.removeEventListener('scroll', this.onViewportChange, true);
    window.removeEventListener('resize', this.onViewportChange);
    document.removeEventListener('click', this.onCaptureClick, true);
  }

  get plusIconSrc(): string {
    return `${this.assetBase()}/add-plus.svg`;
  }

  get filterIconSrc(): string {
    return `${this.assetBase()}/filter.svg`;
  }

  get chevronIconSrc(): string {
    return `${this.assetBase()}/chevron.svg`;
  }

  get leafImageSrc(): string {
    return `${this.assetBase()}/leaf.png`;
  }

  get calendarIconSrc(): string {
    return `${this.assetBase()}/calendar.svg`;
  }

  get dateCalendarIconSrc(): string {
    return `${this.assetBase()}/date-calendar.svg`;
  }

  get statusChevronIconSrc(): string {
    return `${this.assetBase()}/status-chevron.svg`;
  }

  get selectChevronIconSrc(): string {
    return `${this.assetBase()}/select-chevron.svg`;
  }

  get closeIconSrc(): string {
    return `${this.assetBase()}/close.svg`;
  }

  get revokeTrashIconSrc(): string {
    return `${this.assetBase()}/revoke-trash.svg`;
  }

  get revokeLeafImageSrc(): string {
    return `${this.assetBase()}/revoke-leaf.png`;
  }

  get confirmPersonEmail(): string {
    const email = this.confirmTarget()?.email?.trim();
    return email || 'this user';
  }

  get emptyMessage(): string {
    return this.activeTab() === 'delegates'
      ? 'No active delegates found.'
      : 'No owners found';
  }

  get tableLoading(): boolean {
    return this.loading() || this.listLoading();
  }

  private shouldLoadDelegatesFromApi(): boolean {
    return (
      !this.ownersOnly() &&
      !this.isDelegateRole &&
      this.delegates().length === 0
    );
  }

  private shouldLoadOwnersFromApi(): boolean {
    return this.owners().length === 0;
  }

  private async loadOwners(force = false): Promise<void> {
    if (!force && !this.shouldLoadOwnersFromApi()) {
      return;
    }
    const seq = ++this.listRequestSeq;
    this.listLoading.set(true);
    this.cdr.markForCheck();
    try {
      const page = await this.delegateApi.getDelegationsAsDelegate(
        false,
        this.listQuery(),
      );
      if (seq !== this.listRequestSeq) return;
      this.localOwners.set(
        page.items.map((item) => this.mapDelegateItemToOwnerRow(item)),
      );
      this.listTotal = page.totalCount;
    } catch {
      if (seq !== this.listRequestSeq) return;
      this.localOwners.set([]);
      this.listTotal = 0;
    } finally {
      if (seq === this.listRequestSeq) {
        this.listLoading.set(false);
        this.cdr.markForCheck();
      }
    }
  }

  private mapDelegateItemToOwnerRow(
    item: DelegateDelegationItem,
  ): ManageOwnerRow {
    const scopes = (item.scopes ?? []).filter(Boolean);
    return {
      id: item.id,
      delegatorId: item.delegatorId || undefined,
      email: item.email || '—',
      name: item.name || undefined,
      organisationName: item.organisationName || undefined,
      ownerRole: item.ownerRole || undefined,
      scope: scopes[0] ?? '',
      scopes,
      status: this.normalizeDelegateStatus(item.status),
      createdAt: item.createdAt,
      expiryAt: item.expiryAt,
    };
  }

  private async loadDelegates(): Promise<void> {
    if (!this.shouldLoadDelegatesFromApi()) {
      return;
    }
    const seq = ++this.listRequestSeq;
    this.listLoading.set(true);
    this.cdr.markForCheck();
    try {
      const page = await this.delegateApi.getDelegationsAsDelegator(
        this.listQuery(),
      );
      if (seq !== this.listRequestSeq) return;
      this.localDelegates.set(
        page.items.map((item) => this.mapDelegatorItemToRow(item)),
      );
      this.listTotal = page.totalCount;
    } catch {
      if (seq !== this.listRequestSeq) return;
      this.localDelegates.set([]);
      this.listTotal = 0;
    } finally {
      if (seq === this.listRequestSeq) {
        this.listLoading.set(false);
        this.cdr.markForCheck();
      }
    }
  }

  private mapDelegatorItemToRow(
    item: DelegatorDelegationItem,
  ): ManageDelegateRow {
    const scopes = (item.scopes ?? []).filter(Boolean);
    return {
      id: item.id,
      email: item.email || '—',
      name: item.name || undefined,
      scope: scopes[0] ?? '',
      scopes,
      constraintRefs: item.constraintRefs ?? [],
      status: this.normalizeDelegateStatus(item.status),
      createdAt: item.createdAt,
      expiryAt: item.expiryAt,
    };
  }

  private normalizeDelegateStatus(
    value: string,
  ): Exclude<DelegateStatus, 'all'> {
    const raw = (value || '').toLowerCase().replace(/[_-]+/g, ' ').trim();
    if (raw.includes('delet')) return 'deleted';
    if (raw.includes('reject') || raw.includes('revok')) return 'revoked';
    if (raw.includes('expir')) return 'expired';
    if (raw.includes('pend')) return 'pending';
    return 'active';
  }

  get showAddDelegateButton(): boolean {
    return !this.isDelegateRole && this.activeTab() === 'delegates';
  }

  get emailColumnLabel(): string {
    return this.activeTab() === 'owners' ? 'Owner Name' : 'Email';
  }

  get showOwnerOrgColumn(): boolean {
    return this.activeTab() === 'owners';
  }

  get showOwnerRoleColumn(): boolean {
    return this.activeTab() === 'owners';
  }

  get tableColumnCount(): number {
    let count = 6;
    if (this.showOwnerOrgColumn) count += 1;
    if (this.showOwnerRoleColumn) count += 1;
    return count;
  }

  ownerDisplayName(row: ManageDelegateRow | ManageOwnerRow): string {
    const named = 'name' in row ? row.name : undefined;
    return (named && named.trim()) || displayNameFromEmail(row.email);
  }

  ownerOrgName(row: ManageDelegateRow | ManageOwnerRow): string {
    if (this.isPlatformAdminOwner(row)) return '';
    const org = 'organisationName' in row ? row.organisationName : undefined;
    return (org && org.trim()) || '';
  }

  isPlatformAdminOwner(row: ManageDelegateRow | ManageOwnerRow): boolean {
    const role = 'ownerRole' in row ? row.ownerRole : undefined;
    return isPlatformAdminOwnerRole(role);
  }

  ownerRoleLabel(row: ManageDelegateRow | ManageOwnerRow): string {
    const role = 'ownerRole' in row ? row.ownerRole : undefined;
    return ownerRoleDisplayLabel(role) || '—';
  }

  private resolvedDelegates(): ManageDelegateRow[] {
    const fromParent = this.delegates();
    return fromParent.length > 0 ? fromParent : this.localDelegates();
  }

  private resolvedOwners(): ManageOwnerRow[] {
    const fromParent = this.owners();
    return fromParent.length > 0 ? fromParent : this.localOwners();
  }

  get currentRows(): Array<ManageDelegateRow | ManageOwnerRow> {
    return this.activeTab() === 'delegates'
      ? this.resolvedDelegates()
      : this.resolvedOwners();
  }

  scopesFor(row: { scope: string; scopes?: string[] }): string[] {
    return this.scopeStatesFor(row).map((item) => item.label);
  }

  scopeStatesFor(
    row:
      | ManageDelegateRow
      | ManageOwnerRow
      | { scope: string; scopes?: string[] },
  ): OwnerScopeState[] {
    return this.allScopeStatesFor(row).filter((item) =>
      VISIBLE_SCOPE_STATUSES.has(item.status),
    );
  }

  private allScopeStatesFor(
    row:
      | ManageDelegateRow
      | ManageOwnerRow
      | { scope: string; scopes?: string[] },
  ): OwnerScopeState[] {
    if ('ownerRole' in row) {
      return ownerScopeStates(row as ManageOwnerRow);
    }
    const scopes = rowScopes(row);
    const options = this.scopeOptions();
    const labels = options.length
      ? options
          .filter((opt) =>
            scopes.some(
              (scope) =>
                normalizeScopeKey(scope) === normalizeScopeKey(opt.label) ||
                normalizeScopeKey(scope) === normalizeScopeKey(opt.value),
            ),
          )
          .map((opt) => opt.label)
      : scopes;
    const resolved = labels.length ? labels : scopes;
    const status =
      'status' in row ? (row as ManageDelegateRow).status : 'grant';
    const scopeStatus: OwnerScopeState['status'] =
      status === 'pending'
        ? 'pending'
        : status === 'rejected'
          ? 'rejected'
          : status === 'revoked'
            ? 'revoked'
            : status === 'deleted'
              ? 'deleted'
              : 'grant';
    return resolved.map((label) => ({ label, status: scopeStatus }));
  }

  primaryScopeLabel(row: { scope: string; scopes?: string[] }): string {
    const scopes = this.scopesFor(row);
    if (!scopes.length) return '—';
    const label = scopeDisplayLabel(scopes[0]);
    // Truncation ellipsis only when more scopes sit behind the +N chip.
    return this.extraScopeCount(row) > 0 ? `${label}...` : label;
  }

  extraScopeCount(row: { scope: string; scopes?: string[] }): number {
    return Math.max(0, this.scopesFor(row).length - 1);
  }

  formatDateParts(value: string): { date: string; time: string } {
    const formatted = utcToIst((value ?? '').trim());
    if (!formatted) return { date: '—', time: '' };
    const spaceIdx = formatted.indexOf(' ');
    if (spaceIdx === -1) return { date: formatted, time: '' };
    return {
      date: formatted.slice(0, spaceIdx),
      time: formatted.slice(spaceIdx + 1),
    };
  }

  /** Matches My Activities filter-panel: count of non-empty filter groups. */
  get activeFilterCount(): number {
    let count = 0;
    if (
      this.filterDateRangeValue?.startDate &&
      this.filterDateRangeValue?.endDate
    ) {
      count++;
    }
    if (this.selectedStatuses.length > 0 && !this.isAllStatusesSelected()) {
      count++;
    }
    return count;
  }

  get hasStatusSelection(): boolean {
    return this.selectedStatuses.length > 0 && !this.isAllStatusesSelected();
  }

  statusChipLabel(value: Exclude<DelegateStatus, 'all'>): string {
    return this.statusOptions.find((o) => o.value === value)?.label ?? value;
  }

  /** Consumer grants a single scope, so the picker is a fixed selection. */
  get singleAddScope(): DelegateScopeOption | null {
    const options = this.scopeOptions();
    return options.length === 1 ? options[0] : null;
  }

  /** Nothing left to filter once every selectable scope is already chosen. */
  get allAddScopesSelected(): boolean {
    const selectable = this.scopeOptions().filter(
      (opt) => !opt.disabled && !this.isAddScopeAlreadyGranted(opt.value),
    );
    return (
      selectable.length > 0 &&
      selectable.every((opt) => this.selectedAddScopes.includes(opt.value))
    );
  }

  addScopeLabel(value: string): string {
    const options = this.scopeOptions();
    const option = options.find((o) => o.value === value);
    return scopeDisplayLabel(option?.label ?? value);
  }

  get filteredScopeOptions(): DelegateScopeOption[] {
    const query = this.scopeSearchQuery.trim().toLowerCase();
    const options = this.scopeOptions().map((opt) => {
      if (!this.isAddScopeAlreadyGranted(opt.value)) {
        return opt;
      }
      return {
        ...opt,
        disabled: true,
        badge: opt.badge || 'Active',
      };
    });
    if (!query) {
      return options;
    }
    return options.filter((opt) => {
      const haystack = [opt.label, opt.value, this.addScopeDescription(opt)]
        .join(' ')
        .toLowerCase();
      return haystack.includes(query);
    });
  }

  highlightedScopeId(): string | null {
    const opt = this.filteredScopeOptions[this.highlightedScopeIndex];
    return opt ? this.scopeOptionDomId(opt.value) : null;
  }

  scopeOptionDomId(value: string): string {
    return `lib-md-scope-opt-${value}`;
  }

  displayScopeLabel(scope: string): string {
    return scopeDisplayLabel(scope);
  }

  addScopeDescription(opt: DelegateScopeOption): string {
    return (
      opt.description?.trim() ||
      descriptionForScope(opt.value) ||
      descriptionForScope(opt.label)
    );
  }

  scopeDescription(scope: string): string {
    const key = normalizeScopeKey(scope);
    const option = this.scopeOptions().find(
      (opt) =>
        normalizeScopeKey(opt.label) === key ||
        normalizeScopeKey(opt.value) === key,
    );
    if (option) {
      return this.addScopeDescription(option);
    }
    return descriptionForScope(scope);
  }

  showScopeTooltip(event: Event, scope: string): void {
    const text = this.scopeDescription(scope);
    if (!text) {
      this.hideScopeTooltip();
      return;
    }
    const el =
      (event.currentTarget as HTMLElement | null) ??
      (event.target as HTMLElement | null);
    if (!el) {
      return;
    }
    const rect = el.getBoundingClientRect();
    const tipWidth = 320;
    const tipHeight = 28 + Math.ceil(text.length / 46) * 18;
    const gap = 8;
    const left = Math.round(
      Math.min(Math.max(8, rect.left), window.innerWidth - tipWidth - 8),
    );
    const above = rect.top >= tipHeight + gap;
    const top = above
      ? Math.round(rect.top - gap)
      : Math.round(rect.bottom + gap);
    this.scopeTooltipPosition = { top, left, above };
    this.scopeTooltip.set({ label: this.displayScopeLabel(scope), text });
    this.cdr.markForCheck();
  }

  hideScopeTooltip(): void {
    if (!this.scopeTooltip()) {
      return;
    }
    this.scopeTooltip.set(null);
    this.cdr.markForCheck();
  }

  setTab(tab: ManageDelegatesTab): void {
    if (this.ownersOnly() && tab !== 'owners') return;
    if (this.activeTab() === tab) return;
    this.activeTab.set(tab);
    persistTab(this.tabStorageScope(), tab);
    this.closeActionMenu();
    this.closeScopesPopup();
    this.statusDropdownOpen.set(false);
    this.dateRangeOpen.set(false);
    this.filtersOpen.set(false);
    if (tab === 'owners') {
      this.showAddModal.set(false);
    }
    this.currentPage = 1;
    this.listTotal = 0;
    this.tabChange.emit(tab);
    this.cdr.markForCheck();
    if (tab === 'delegates') {
      void this.loadDelegates();
    }
    if (tab === 'owners') {
      void this.loadOwners();
    }
  }

  toggleFilters(): void {
    this.filtersOpen.update((v) => !v);
    if (!this.filtersOpen()) {
      this.statusDropdownOpen.set(false);
      this.dateRangeOpen.set(false);
    }
  }

  clearAllFilters(event: Event): void {
    event.stopPropagation();
    this.filterDateRangeValue = null;
    this.selectedStatuses = [];
    this.statusDropdownOpen.set(false);
    this.dateRangeOpen.set(false);
    this.emitFilters();
    this.reloadFromFilters();
  }

  removeStatus(value: Exclude<DelegateStatus, 'all'>, event: Event): void {
    event.stopPropagation();
    this.selectedStatuses = this.selectedStatuses.filter((s) => s !== value);
    this.emitFilters();
    this.reloadFromFilters();
  }

  clearStatusSelection(event: Event): void {
    event.stopPropagation();
    this.selectedStatuses = [];
    this.emitFilters();
    this.reloadFromFilters();
  }

  onActivitiesDateRangeChange(range: DateRange): void {
    this.filterDateRangeValue = range;
    this.emitFilters();
    const complete = !!(range?.startDate && range?.endDate);
    const cleared = !range?.startDate && !range?.endDate;
    if (complete || cleared) {
      this.reloadFromFilters();
    }
  }

  onDateRangePickerOpen(): void {
    this.dateRangeOpen.set(true);
    this.statusDropdownOpen.set(false);
    this.closeActionMenu();
  }

  onDateRangePickerClose(): void {
    this.dateRangeOpen.set(false);
  }

  onExpiryDateChange(range: DateRange): void {
    this.expiryDateRangeValue = range;
    const start = range?.startDate;
    this.addForm.expiryAt = start ? this.toIsoDate(start) : '';
    this.clearAddFieldError('expiryAt');
    this.cdr.markForCheck();
  }

  toggleStatusDropdown(event: Event): void {
    event.stopPropagation();
    this.statusDropdownOpen.update((v) => !v);
    this.closeActionMenu();
  }

  isStatusChecked(value: DelegateStatus): boolean {
    if (value === 'all') {
      return this.isAllStatusesSelected() || this.selectedStatuses.length === 0;
    }
    return this.selectedStatuses.includes(value);
  }

  toggleStatus(value: DelegateStatus, event?: Event): void {
    event?.stopPropagation();
    if (value === 'all') {
      this.selectedStatuses = [];
    } else {
      const idx = this.selectedStatuses.indexOf(value);
      if (idx >= 0) {
        this.selectedStatuses = this.selectedStatuses.filter(
          (s) => s !== value,
        );
      } else {
        this.selectedStatuses = [...this.selectedStatuses, value];
      }
      const concrete = this.statusOptions
        .filter((o) => o.value !== 'all')
        .map((o) => o.value as Exclude<DelegateStatus, 'all'>);
      if (concrete.every((s) => this.selectedStatuses.includes(s))) {
        this.selectedStatuses = [];
      }
    }
    this.emitFilters();
    this.reloadFromFilters();
  }

  openAddDelegate(): void {
    if (this.isDelegateRole) {
      return;
    }
    this.addForm = { email: '', scope: '', expiryAt: '', comments: '' };
    this.selectedAddScopes = [];
    this.addFormErrors = {};
    this.expiryDateRangeValue = null;
    this.applySingleAddScope();
    this.closeScopeDropdown();
    this.showAddModal.set(true);
    this.statusDropdownOpen.set(false);
  }

  closeAddDelegate(): void {
    if (this.addSubmitting()) {
      return;
    }
    this.showAddModal.set(false);
    this.closeScopeDropdown();
    this.addFormErrors = {};
  }

  openScopeDropdown(): void {
    if (this.singleAddScope) {
      return;
    }
    if (!this.scopeDropdownOpen()) {
      this.scopeDropdownOpen.set(true);
      this.highlightedScopeIndex = this.firstEnabledScopeIndex();
    }
    this.scopeTriggerEl =
      this.scopeTriggerEl ??
      document.querySelector<HTMLElement>(
        '.lib-manage-delegates .lib-md--scope-trigger',
      );
    this.syncScopeMenuPosition();
    this.scheduleScopeMenuPosition();
    this.cdr.markForCheck();
  }

  closeScopeDropdown(): void {
    this.scopeDropdownOpen.set(false);
    this.scopeSearchQuery = '';
    this.highlightedScopeIndex = 0;
    this.scopeTriggerEl = null;
  }

  toggleScopeDropdown(event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    if (this.scopeDropdownOpen()) {
      this.closeScopeDropdown();
      this.cdr.markForCheck();
      return;
    }
    this.openScopeDropdown();
    this.focusScopeSearch(event);
  }

  onScopeTriggerClick(event: MouseEvent): void {
    if (this.singleAddScope) {
      return;
    }
    const target = event.target as HTMLElement | null;
    if (
      target?.closest('.lib-md--filter-chip-remove, .lib-md--scope-chevron-btn')
    ) {
      return;
    }
    this.openScopeDropdown();
    this.focusScopeSearch(event);
  }

  onScopeSearchQueryChange(): void {
    this.openScopeDropdown();
    this.highlightedScopeIndex = this.firstEnabledScopeIndex();
    this.cdr.markForCheck();
  }

  onScopeSearchKeydown(event: KeyboardEvent): void {
    const options = this.filteredScopeOptions;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeScopeDropdown();
      this.cdr.markForCheck();
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.openScopeDropdown();
      this.highlightedScopeIndex = this.nextEnabledScopeIndex(
        this.highlightedScopeIndex,
        1,
      );
      this.cdr.markForCheck();
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.openScopeDropdown();
      this.highlightedScopeIndex = this.nextEnabledScopeIndex(
        this.highlightedScopeIndex,
        -1,
      );
      this.cdr.markForCheck();
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      const opt = options[this.highlightedScopeIndex];
      if (opt && !opt.disabled) {
        this.selectScope(opt.value, event);
      }
      return;
    }
    if (
      event.key === 'Backspace' &&
      !this.scopeSearchQuery &&
      this.selectedAddScopes.length
    ) {
      event.preventDefault();
      this.removeAddScope(
        this.selectedAddScopes[this.selectedAddScopes.length - 1],
        event,
      );
    }
  }

  /** Keep the modal from closing, but collapse Choose scope when the click is outside it. */
  onAddModalClick(event: MouseEvent): void {
    event.stopPropagation();
    const target = event.target as HTMLElement | null;
    if (
      !target?.closest('.lib-md--scope-dd') &&
      !this.isScopePickerTarget(target)
    ) {
      this.closeScopeDropdown();
    }
  }

  selectScope(value: string, event?: Event): void {
    event?.stopPropagation();
    const option = this.filteredScopeOptions.find(
      (item) => item.value === value,
    );
    if (option?.disabled || this.isAddScopeAlreadyGranted(value)) {
      return;
    }
    if (this.selectedAddScopes.includes(value)) {
      this.selectedAddScopes = this.selectedAddScopes.filter(
        (item) => item !== value,
      );
    } else {
      this.selectedAddScopes = [...this.selectedAddScopes, value];
    }
    this.addForm.scope = this.selectedAddScopes[0] ?? '';
    this.scopeSearchQuery = '';
    this.highlightedScopeIndex = this.firstEnabledScopeIndex();
    this.clearAddFieldError('scope');
    if (this.allAddScopesSelected) {
      this.closeScopeDropdown();
    } else {
      this.scheduleScopeMenuPosition();
    }
    this.cdr.markForCheck();
  }

  clearScopeSelection(event: Event): void {
    event.stopPropagation();
    if (this.singleAddScope) {
      return;
    }
    this.selectedAddScopes = [];
    this.addForm.scope = '';
    this.clearAddFieldError('scope');
    this.cdr.markForCheck();
  }

  removeAddScope(value: string, event: Event): void {
    event.stopPropagation();
    if (this.singleAddScope) {
      return;
    }
    this.selectedAddScopes = this.selectedAddScopes.filter(
      (item) => item !== value,
    );
    this.addForm.scope = this.selectedAddScopes[0] ?? '';
    this.clearAddFieldError('scope');
    this.cdr.markForCheck();
  }

  private focusScopeSearch(event: Event): void {
    const host =
      (event.currentTarget as HTMLElement | null)?.closest(
        '.lib-md--scope-dd',
      ) ??
      (event.target as HTMLElement | null)?.closest('.lib-md--scope-dd') ??
      null;
    const trigger = host?.querySelector<HTMLElement>('.lib-md--scope-trigger');
    if (trigger) {
      this.scopeTriggerEl = trigger;
    }
    host?.querySelector<HTMLInputElement>('.lib-md--scope-search')?.focus();
    this.scheduleScopeMenuPosition();
  }

  private isScopePickerTarget(target: HTMLElement | null): boolean {
    return !!target?.closest('.lib-md--scope-dd, .lib-md--scope-menu');
  }

  private scheduleScopeMenuPosition(): void {
    requestAnimationFrame(() => {
      this.syncScopeMenuPosition();
      this.cdr.markForCheck();
    });
  }

  private syncScopeMenuPosition(): void {
    if (!this.scopeDropdownOpen()) {
      return;
    }
    const trigger =
      this.scopeTriggerEl ??
      document.querySelector<HTMLElement>(
        '.lib-manage-delegates .lib-md--scope-trigger',
      );
    if (!trigger) {
      return;
    }
    this.scopeTriggerEl = trigger;
    const rect = trigger.getBoundingClientRect();
    const gap = 4;
    const pad = 12;
    const below = window.innerHeight - rect.bottom - gap - pad;
    const above = rect.top - gap - pad;
    const openBelow = below >= 160 || below >= above;
    const maxHeight = Math.max(140, Math.min(280, openBelow ? below : above));
    this.scopeMenuPosition = {
      top: openBelow
        ? Math.round(rect.bottom + gap)
        : Math.round(rect.top - gap - maxHeight),
      left: Math.round(rect.left),
      width: Math.round(rect.width),
      maxHeight: Math.round(maxHeight),
    };
  }

  private firstEnabledScopeIndex(): number {
    return this.filteredScopeOptions.findIndex((opt) => !opt.disabled);
  }

  private nextEnabledScopeIndex(from: number, direction: 1 | -1): number {
    const options = this.filteredScopeOptions;
    if (!options.length) {
      return 0;
    }
    let i = from;
    for (let n = 0; n < options.length; n++) {
      i = (i + direction + options.length) % options.length;
      if (!options[i]?.disabled) {
        return i;
      }
    }
    return from;
  }

  clearAddFieldError(field: keyof AddDelegateFormErrors): void {
    if (this.addFormErrors[field]) {
      this.addFormErrors = { ...this.addFormErrors, [field]: undefined };
      this.cdr.markForCheck();
    }
  }

  onAddEmailChange(): void {
    this.clearAddFieldError('email');
    this.dropGrantedAddScopes();
    this.applySingleAddScope();
    this.cdr.markForCheck();
  }

  isAddScopeAlreadyGranted(value: string): boolean {
    const existing = this.existingDelegateForAdd;
    if (!existing) {
      return false;
    }
    const held = new Set(
      this.grantedScopeValues(existing).map(normalizeScopeKey),
    );
    return held.has(normalizeScopeKey(value));
  }

  async submitAddDelegate(): Promise<void> {
    if (this.addSubmitting()) {
      return;
    }
    const errors = this.validateAddForm();
    this.addFormErrors = errors;
    this.cdr.markForCheck();
    if (Object.keys(errors).length > 0) {
      return;
    }

    const form: AddDelegateForm = {
      ...this.addForm,
      email: this.addForm.email.trim(),
      comments: this.addForm.comments.trim(),
    };
    this.addSubmitting.set(true);
    this.cdr.markForCheck();

    try {
      let userSub: string | null = null;
      try {
        const response = await this.delegateApi.getUserByEmail(form.email);
        userSub = this.delegateApi.extractSub(response);
      } catch (error: unknown) {
        this.addFormErrors = {
          ...this.addFormErrors,
          email: this.lookupEmailError(error),
        };
        return;
      }
      if (!userSub) {
        this.addFormErrors = {
          ...this.addFormErrors,
          email: 'No user found for this email.',
        };
        return;
      }

      form.userSub = userSub;
      const role =
        toDelegationRole(this.tabStorageScope()) ??
        (await this.resolveDelegatorRole());
      if (!role) {
        this.addFormErrors = {
          ...this.addFormErrors,
          submit: 'Could not determine your role. Try again.',
        };
        return;
      }

      const expiryAt = this.toExpiryDateTime(form.expiryAt);
      const existing = this.findActiveDelegateByEmail(form.email);
      try {
        if (existing) {
          const newScopes = this.scopesNotAlreadyGranted(
            existing,
            this.selectedAddScopes,
          );
          if (!newScopes.length) {
            this.addFormErrors = {
              ...this.addFormErrors,
              scope: 'This user already has these access scopes.',
            };
            return;
          }
          await this.delegateApi.appendDelegationConstraints(existing.id, {
            roles: this.buildDelegationRoles(role, newScopes, expiryAt),
          });
        } else {
          await this.delegateApi.createDelegation({
            delegateId: userSub,
            justification: form.comments,
            expiryAt,
            roles: this.buildDelegationRoles(role, this.selectedAddScopes),
          });
        }
      } catch (error: unknown) {
        this.addFormErrors = {
          ...this.addFormErrors,
          submit:
            this.extractHttpMessage(error) ||
            (existing
              ? 'Could not update delegate. Try again.'
              : 'Could not create delegate. Try again.'),
        };
        return;
      }

      form.scope =
        this.selectedAddScopes.map((value) => this.addScopeLabel(value))[0] ??
        form.scope;

      this.addDelegate.emit(form);
      this.addSubmitting.set(false);
      this.closeAddDelegate();
      void this.loadDelegates();
    } finally {
      this.addSubmitting.set(false);
      this.cdr.markForCheck();
    }
  }

  toggleActionMenu(
    event: Event,
    row: ManageDelegateRow | ManageOwnerRow,
  ): void {
    event.preventDefault();
    event.stopPropagation();
    if (!this.canUseRowAction(row)) {
      return;
    }
    this.statusDropdownOpen.set(false);
    this.closeScopesPopup();
    const button =
      (event.currentTarget as HTMLElement | null) ??
      ((event.target as HTMLElement).closest(
        '.action-btn',
      ) as HTMLElement | null);
    if (this.openActionId() === row.id) {
      this.closeActionMenu();
      return;
    }
    this.actionAnchor = button;
    this.openActionId.set(row.id);
    this.openActionRow.set(row);
    this.syncActionDropdownPosition();
  }

  closeActionMenu(): void {
    this.openActionId.set(null);
    this.openActionRow.set(null);
    this.actionAnchor = null;
  }

  private syncActionDropdownPosition(): void {
    if (!this.openActionId() || !this.actionAnchor) return;
    const rect = this.actionAnchor.getBoundingClientRect();
    const menuWidth = 188;
    this.dropdownPosition = {
      top: Math.round(rect.bottom + 4),
      left: Math.round(Math.max(8, rect.right - menuWidth)),
    };
    this.cdr.markForCheck();
  }

  toggleScopesPopup(
    event: Event,
    row: ManageDelegateRow | ManageOwnerRow,
  ): void {
    event.stopPropagation();
    this.closeActionMenu();
    this.hideScopeTooltip();
    const button =
      (event.currentTarget as HTMLElement | null) ??
      ((event.target as HTMLElement).closest(
        '.lib-md--scope-more',
      ) as HTMLElement | null);
    if (this.openScopesRowId() === row.id) {
      this.closeScopesPopup();
      return;
    }
    this.scopesAnchor = button;
    this.openScopesRowId.set(row.id);
    this.openScopesRow.set(row);
    this.syncScopesPopupPosition();
  }

  closeScopesPopup(): void {
    this.openScopesRowId.set(null);
    this.openScopesRow.set(null);
    this.scopesAnchor = null;
    this.hideScopeTooltip();
  }

  isScopesPopupOpen(id: string): boolean {
    return this.openScopesRowId() === id;
  }

  private syncScopesPopupPosition(): void {
    const row = this.openScopesRow();
    if (!this.openScopesRowId() || !this.scopesAnchor || !row) return;
    const rect = this.scopesAnchor.getBoundingClientRect();
    const menuWidth = 280;
    const itemCount = Math.max(1, this.scopeStatesFor(row).length);
    const menuHeight = Math.min(280, 44 + itemCount * 42);
    const spaceBelow = window.innerHeight - rect.bottom - 8;
    const openUp = spaceBelow < menuHeight && rect.top > menuHeight + 8;
    const top = openUp
      ? Math.round(rect.top - menuHeight - 6)
      : Math.round(rect.bottom + 6);
    const centered = rect.left + rect.width / 2 - menuWidth / 2;
    const left = Math.round(
      Math.min(Math.max(8, centered), window.innerWidth - menuWidth - 8),
    );
    this.scopesPopupPosition = { top, left };
    this.cdr.markForCheck();
  }

  isActionOpen(id: string): boolean {
    return this.openActionId() === id;
  }

  canUseRowAction(row: ManageDelegateRow | ManageOwnerRow): boolean {
    const status = (row.status || '').toLowerCase();
    return (
      status !== 'rejected' &&
      status !== 'revoked' &&
      status !== 'deleted' &&
      status !== 'expired'
    );
  }

  canDeleteDelegate(row: ManageDelegateRow): boolean {
    const status = (row.status || '').toLowerCase();
    return status !== 'deleted' && status !== 'expired';
  }

  canDowngradeDelegate(row: ManageDelegateRow): boolean {
    const status = (row.status || '').toLowerCase();
    if (status !== 'active' && status !== 'pending') {
      return false;
    }
    return this.constraintCount(row) > 1;
  }

  canUpgradeDelegate(row: ManageDelegateRow): boolean {
    const status = (row.status || '').toLowerCase();
    if (status !== 'active' && status !== 'pending') {
      return false;
    }
    return this.upgradeableScopeValues(row).length > 0;
  }

  onDeleteDelegate(row: ManageDelegateRow): void {
    if (!this.canDeleteDelegate(row)) {
      return;
    }
    this.closeActionMenu();
    this.confirmMode.set('delete');
    this.confirmTarget.set(row);
    this.showConfirmModal.set(true);
  }

  onDowngradeDelegate(row: ManageDelegateRow): void {
    if (!this.canDowngradeDelegate(row)) {
      return;
    }
    this.closeActionMenu();
    this.downgradeTarget.set(row);
    this.selectedDowngradeScopes = [...this.grantedScopeValues(row)];
    this.downgradeError = '';
    this.showDowngradeModal.set(true);
    this.cdr.markForCheck();
  }

  closeDowngradeModal(): void {
    if (this.downgradeSubmitting()) {
      return;
    }
    this.showDowngradeModal.set(false);
    this.downgradeTarget.set(null);
    this.selectedDowngradeScopes = [];
    this.downgradeError = '';
  }

  downgradeScopeItems(): string[] {
    const row = this.downgradeTarget();
    return row ? this.grantedScopeValues(row) : [];
  }

  isDowngradeScopeSelected(scope: string): boolean {
    return this.selectedDowngradeScopes.some(
      (item) => normalizeScopeKey(item) === normalizeScopeKey(scope),
    );
  }

  isRemovingDowngradeScope(scope: string): boolean {
    return !this.isDowngradeScopeSelected(scope);
  }

  toggleDowngradeScope(scope: string, event?: Event): void {
    event?.stopPropagation();
    if (this.isDowngradeScopeSelected(scope)) {
      this.selectedDowngradeScopes = this.selectedDowngradeScopes.filter(
        (item) => normalizeScopeKey(item) !== normalizeScopeKey(scope),
      );
    } else {
      this.selectedDowngradeScopes = [...this.selectedDowngradeScopes, scope];
    }
    this.downgradeError = '';
    this.cdr.markForCheck();
  }

  pendingDowngradeRemovals(): string[] {
    const row = this.downgradeTarget();
    if (!row) {
      return [];
    }
    const kept = new Set(this.selectedDowngradeScopes.map(normalizeScopeKey));
    return this.grantedScopeValues(row).filter(
      (scope) => !kept.has(normalizeScopeKey(scope)),
    );
  }

  get canSubmitDowngrade(): boolean {
    return (
      this.pendingDowngradeRemovals().length > 0 && !this.downgradeSubmitting()
    );
  }

  get willDeleteGrantOnDowngrade(): boolean {
    const row = this.downgradeTarget();
    if (!row) {
      return false;
    }
    const granted = this.constraintCount(row);
    const removing = this.pendingDowngradeRemovals().length;
    return granted > 0 && (granted === 1 || removing >= granted);
  }

  async submitDowngrade(): Promise<void> {
    const row = this.downgradeTarget();
    const removed = this.pendingDowngradeRemovals();
    if (!row || !removed.length || this.downgradeSubmitting()) {
      return;
    }

    const role =
      toDelegationRole(this.tabStorageScope()) ??
      (await this.resolveDelegatorRole());
    if (!role) {
      this.downgradeError = 'Could not determine your role. Try again.';
      this.cdr.markForCheck();
      return;
    }

    this.downgradeSubmitting.set(true);
    this.downgradeError = '';
    this.cdr.markForCheck();
    try {
      if (this.willDeleteGrantOnDowngrade) {
        await this.delegateApi.deleteDelegation(row.id);
        this.deleteDelegate.emit(row);
      } else {
        await this.delegateApi.deleteDelegationConstraints(
          row.id,
          this.buildDeleteConstraintsPayload(role, row, removed),
        );
      }
      this.downgradeSubmitting.set(false);
      this.closeDowngradeModal();
      void this.loadDelegates();
    } catch (error: unknown) {
      this.downgradeError =
        this.extractHttpMessage(error) || 'Could not update scopes. Try again.';
    } finally {
      this.downgradeSubmitting.set(false);
      this.cdr.markForCheck();
    }
  }

  onUpgradeDelegate(row: ManageDelegateRow): void {
    if (!this.canUpgradeDelegate(row)) {
      return;
    }
    this.closeActionMenu();
    this.upgradeTarget.set(row);
    this.selectedUpgradeScopes = [];
    this.upgradeError = '';
    this.showUpgradeModal.set(true);
    this.cdr.markForCheck();
  }

  closeUpgradeModal(): void {
    if (this.upgradeSubmitting()) {
      return;
    }
    this.showUpgradeModal.set(false);
    this.upgradeTarget.set(null);
    this.selectedUpgradeScopes = [];
    this.upgradeError = '';
  }

  upgradeScopeItems(): string[] {
    const row = this.upgradeTarget();
    return row ? this.upgradeableScopeValues(row) : [];
  }

  isUpgradeScopeSelected(scope: string): boolean {
    return this.selectedUpgradeScopes.some(
      (item) => normalizeScopeKey(item) === normalizeScopeKey(scope),
    );
  }

  toggleUpgradeScope(scope: string, event?: Event): void {
    event?.stopPropagation();
    if (this.isUpgradeScopeSelected(scope)) {
      this.selectedUpgradeScopes = this.selectedUpgradeScopes.filter(
        (item) => normalizeScopeKey(item) !== normalizeScopeKey(scope),
      );
    } else {
      this.selectedUpgradeScopes = [...this.selectedUpgradeScopes, scope];
    }
    this.upgradeError = '';
    this.cdr.markForCheck();
  }

  get canSubmitUpgrade(): boolean {
    return this.selectedUpgradeScopes.length > 0 && !this.upgradeSubmitting();
  }

  async submitUpgrade(): Promise<void> {
    const row = this.upgradeTarget();
    const added = [...this.selectedUpgradeScopes];
    if (!row || !added.length || this.upgradeSubmitting()) {
      return;
    }

    const role =
      toDelegationRole(this.tabStorageScope()) ??
      (await this.resolveDelegatorRole());
    if (!role) {
      this.upgradeError = 'Could not determine your role. Try again.';
      this.cdr.markForCheck();
      return;
    }

    this.upgradeSubmitting.set(true);
    this.upgradeError = '';
    this.cdr.markForCheck();
    try {
      const expiryAt = this.toExpiryDateTime(row.expiryAt);
      await this.delegateApi.appendDelegationConstraints(row.id, {
        roles: this.buildDelegationRoles(role, added, expiryAt || undefined),
      });
      this.upgradeSubmitting.set(false);
      this.closeUpgradeModal();
      void this.loadDelegates();
    } catch (error: unknown) {
      this.upgradeError =
        this.extractHttpMessage(error) || 'Could not add scopes. Try again.';
    } finally {
      this.upgradeSubmitting.set(false);
      this.cdr.markForCheck();
    }
  }

  onRevokeOwner(row: ManageOwnerRow): void {
    this.closeActionMenu();
    this.confirmMode.set('revoke');
    this.confirmTarget.set(row);
    this.showConfirmModal.set(true);
  }

  onSwitchOwner(row: ManageOwnerRow): void {
    this.closeActionMenu();
    this.switchOwner.emit(row);
  }

  canRevokeOwner(row: ManageDelegateRow | ManageOwnerRow): boolean {
    return this.isActionableOwnerStatus(row.status);
  }

  canSwitchOwner(row: ManageDelegateRow | ManageOwnerRow): boolean {
    if (!this.isActionableOwnerStatus(row.status)) {
      return false;
    }
    const activeId = (this.activeOwnerId() || '').trim();
    return !activeId || row.id !== activeId;
  }

  private isActionableOwnerStatus(status: string): boolean {
    const raw = (status || '').toLowerCase();
    return raw === 'active' || raw === 'pending' || raw === 'grant';
  }

  canRequestOwner(row: ManageDelegateRow | ManageOwnerRow): boolean {
    if (!('ownerRole' in row) || isConsumerOwnerRole(row.ownerRole)) {
      return false;
    }
    return scopesForOwnerRole(row.ownerRole).length > 0;
  }

  onRequestOwner(row: ManageOwnerRow): void {
    this.closeActionMenu();
    this.requestTarget.set(row);
    this.selectedRequestScopes = [...grantedOwnerScopes(row)];
    this.showRequestModal.set(true);
    this.cdr.markForCheck();
  }

  closeRequestModal(): void {
    this.showRequestModal.set(false);
    this.requestTarget.set(null);
    this.selectedRequestScopes = [];
  }

  requestScopeItems(): RequestScopeItem[] {
    const owner = this.requestTarget();
    if (!owner) return [];
    const granted = new Set(
      grantedOwnerScopes(owner).map((scope) => normalizeScopeKey(scope)),
    );
    return scopesForOwnerRole(owner.ownerRole).map((label) => ({
      label,
      granted: granted.has(normalizeScopeKey(label)),
    }));
  }

  isRequestScopeSelected(scope: string): boolean {
    return this.selectedRequestScopes.includes(scope);
  }

  isRevokingGrantedScope(item: RequestScopeItem): boolean {
    return item.granted && !this.isRequestScopeSelected(item.label);
  }

  isRequestingNewScope(item: RequestScopeItem): boolean {
    return !item.granted && this.isRequestScopeSelected(item.label);
  }

  toggleRequestScope(scope: string, event?: Event): void {
    event?.stopPropagation();
    if (this.selectedRequestScopes.includes(scope)) {
      this.selectedRequestScopes = this.selectedRequestScopes.filter(
        (item) => item !== scope,
      );
    } else {
      this.selectedRequestScopes = [...this.selectedRequestScopes, scope];
    }
    this.cdr.markForCheck();
  }

  pendingRequestScopes(): string[] {
    const owner = this.requestTarget();
    if (!owner) return [];
    const granted = new Set(
      grantedOwnerScopes(owner).map((scope) => normalizeScopeKey(scope)),
    );
    return this.selectedRequestScopes.filter(
      (scope) => !granted.has(normalizeScopeKey(scope)),
    );
  }

  pendingRevokeScopes(): string[] {
    const owner = this.requestTarget();
    if (!owner) return [];
    const selected = new Set(
      this.selectedRequestScopes.map((scope) => normalizeScopeKey(scope)),
    );
    return grantedOwnerScopes(owner).filter(
      (scope) => !selected.has(normalizeScopeKey(scope)),
    );
  }

  get canSubmitScopeRequest(): boolean {
    return (
      this.pendingRequestScopes().length > 0 ||
      this.pendingRevokeScopes().length > 0
    );
  }

  submitScopeRequest(): void {
    const owner = this.requestTarget();
    if (!owner || !this.canSubmitScopeRequest) return;
    const requestScopes = this.pendingRequestScopes();
    const revokeScopes = this.pendingRevokeScopes();
    this.requestOwner.emit({
      owner,
      scopes: requestScopes,
      requestScopes,
      revokeScopes,
    });
    this.closeRequestModal();
  }

  closeConfirmModal(): void {
    if (this.confirmSubmitting()) {
      return;
    }
    this.showConfirmModal.set(false);
    this.confirmTarget.set(null);
  }

  async confirmDestructiveAction(): Promise<void> {
    const target = this.confirmTarget();
    if (!target || this.confirmSubmitting()) {
      return;
    }
    if (this.confirmMode() === 'delete') {
      const delegationId = String(target.id ?? '').trim();
      if (!delegationId) {
        this.closeConfirmModal();
        return;
      }
      this.confirmSubmitting.set(true);
      this.cdr.markForCheck();
      try {
        await this.delegateApi.deleteDelegation(delegationId);
      } catch {
        return;
      } finally {
        this.confirmSubmitting.set(false);
        this.cdr.markForCheck();
      }
      if (this.delegates().length === 0) {
        this.localDelegates.update((rows) =>
          rows.filter((r) => r.id !== target.id),
        );
      }
      this.deleteDelegate.emit(target as ManageDelegateRow);
      this.closeConfirmModal();
      void this.loadDelegates();
      return;
    }

    const delegationId = String(target.id ?? '').trim();
    if (!delegationId) {
      this.closeConfirmModal();
      return;
    }
    this.confirmSubmitting.set(true);
    this.cdr.markForCheck();
    try {
      await this.delegateApi.rejectDelegation(delegationId);
    } catch {
      return;
    } finally {
      this.confirmSubmitting.set(false);
      this.cdr.markForCheck();
    }
    this.localOwners.update((rows) =>
      rows.map((row) =>
        row.id === target.id ? { ...row, status: 'revoked' } : row,
      ),
    );
    this.withdrawOwner.emit(target as ManageOwnerRow);
    this.closeConfirmModal();
    void this.loadOwners(true);
  }

  statusLabel(status: string): string {
    if (!status) return '—';
    const key = status.toLowerCase();
    if (key === 'grant' || key === 'granted') return 'Granted';
    if (key === 'rejected') return 'Revoked';
    return status.charAt(0).toUpperCase() + status.slice(1);
  }

  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (
      target?.closest('.lib-md--action-trigger') ||
      target?.closest('.lib-md--action-menu')
    ) {
      return;
    }
    this.closeActionMenu();
    if (
      target?.closest('.lib-md--scope-more') ||
      target?.closest('.lib-md--scopes-popup')
    ) {
      return;
    }
    if (this.openScopesRowId()) {
      this.closeScopesPopup();
    }
    if (this.statusDropdownOpen()) {
      this.statusDropdownOpen.set(false);
    }
    if (this.scopeDropdownOpen() && !this.isScopePickerTarget(target)) {
      this.closeScopeDropdown();
    }
  }

  private validateAddForm(): AddDelegateFormErrors {
    const errors: AddDelegateFormErrors = {};
    const email = this.addForm.email?.trim() ?? '';
    if (!email) {
      errors.email = 'Email Id is required';
    } else if (!this.isValidEmail(email)) {
      errors.email = 'Enter a valid email address';
    } else if (this.isOwnEmail(email)) {
      errors.email = 'You cannot create a delegate for yourself.';
    }
    const existing = this.findActiveDelegateByEmail(email);
    const newScopes = existing
      ? this.scopesNotAlreadyGranted(existing, this.selectedAddScopes)
      : this.selectedAddScopes;
    if (!this.selectedAddScopes.length) {
      errors.scope = 'Access scope is required';
    } else if (existing && !newScopes.length) {
      errors.scope = 'This user already has these access scopes.';
    }
    if (!this.addForm.expiryAt?.trim()) {
      errors.expiryAt = 'Expiry Date is required';
    }
    const comments = this.addForm.comments?.trim() ?? '';
    if (!comments) {
      errors.comments = 'Comments is required';
    } else if (comments.length > this.commentsMaxLength) {
      errors.comments = `Comments cannot exceed ${this.commentsMaxLength} characters`;
    }
    return errors;
  }

  private extractHttpStatus(error: unknown): number | null {
    if (!error || typeof error !== 'object') {
      return null;
    }
    const status = (error as { status?: unknown }).status;
    return typeof status === 'number' ? status : null;
  }

  private extractHttpMessage(error: unknown): string {
    if (!error || typeof error !== 'object') {
      return '';
    }
    const httpError = error as { error?: unknown; message?: unknown };
    const body = httpError.error;
    if (typeof body === 'string' && body.trim()) {
      return body.trim();
    }
    if (body && typeof body === 'object') {
      const record = body as Record<string, unknown>;
      for (const key of ['detail', 'title', 'message']) {
        const value = record[key];
        if (typeof value === 'string' && value.trim()) {
          return value.trim();
        }
      }
    }
    return typeof httpError.message === 'string'
      ? httpError.message.trim()
      : '';
  }

  private lookupEmailError(error: unknown): string {
    const status = this.extractHttpStatus(error);
    if (status === 404) {
      return 'No user found for this email.';
    }
    if (status === 400) {
      return 'Enter a valid email address';
    }
    return (
      this.extractHttpMessage(error) ||
      'Could not look up this email. Try again.'
    );
  }

  /**
   * One `roles[]` object per API role. Data Access and Asset Management therefore
   * become two entries: `{ role: "consumer" }` and `{ role: "provider" }`.
   */
  private buildDelegationRoles(
    delegatorRole: DelegationRole,
    scopes: string[],
    expiryAt?: string,
  ): CreateDelegationRole[] {
    return groupScopesByDelegationRole(
      delegatorRole,
      scopes.filter((scope) => {
        const option = this.scopeOptions().find((item) => item.value === scope);
        return !option?.disabled;
      }),
    ).map((group) => ({
      role: toDelegationRole(group.role) ?? delegatorRole,
      constraints: group.scopes.map((scope) => ({
        scope,
        ...(expiryAt ? { expiryAt } : {}),
      })),
    }));
  }

  get existingDelegateForAdd(): ManageDelegateRow | null {
    return this.findActiveDelegateByEmail(this.addForm.email);
  }

  private findActiveDelegateByEmail(email: string): ManageDelegateRow | null {
    const key = email.trim().toLowerCase();
    if (!key) {
      return null;
    }
    return (
      this.resolvedDelegates().find((row) => {
        if ((row.email || '').trim().toLowerCase() !== key) {
          return false;
        }
        const status = (row.status || '').toLowerCase();
        return status === 'active' || status === 'pending';
      }) ?? null
    );
  }

  private scopesNotAlreadyGranted(
    row: ManageDelegateRow,
    scopes: string[],
  ): string[] {
    const held = new Set(this.grantedScopeValues(row).map(normalizeScopeKey));
    return scopes.filter((scope) => !held.has(normalizeScopeKey(scope)));
  }

  private constraintCount(row: ManageDelegateRow): number {
    const refs = row.constraintRefs ?? [];
    if (refs.length) {
      const keys = new Set(
        refs.map((ref) => normalizeScopeKey(ref.scope)).filter(Boolean),
      );
      if (!keys.size) return refs.length;
      const catalog = this.scopeOptions().map((opt) => opt.value);
      for (const key of [...keys]) {
        if (isBundledCompanionScope(key, catalog)) keys.delete(key);
      }
      return keys.size;
    }
    return this.grantedScopeValues(row).length || rowScopes(row).length;
  }

  private buildDeleteConstraintsPayload(
    delegatorRole: DelegationRole,
    row: ManageDelegateRow,
    scopes: string[],
  ): DeleteDelegationConstraintsPayload {
    const toRemove = new Set(
      withBundledDelegationScopes(scopes).map(normalizeScopeKey),
    );
    const fromRefs = (row.constraintRefs ?? []).filter((ref) =>
      toRemove.has(normalizeScopeKey(ref.scope)),
    );
    if (fromRefs.length) {
      const grouped = new Map<
        string,
        Array<{ scope: string; entityId?: string[]; entityType?: string }>
      >();
      for (const ref of fromRefs) {
        const apiRole = toDelegationRole(ref.role) ?? delegatorRole;
        const list = grouped.get(apiRole) ?? [];
        list.push({
          scope: ref.scope,
          ...(ref.entityId?.length ? { entityId: ref.entityId } : {}),
          ...(ref.entityType ? { entityType: ref.entityType } : {}),
        });
        grouped.set(apiRole, list);
      }
      return {
        roles: [...grouped.entries()].map(([role, constraints]) => ({
          role: toDelegationRole(role) ?? delegatorRole,
          constraints,
        })),
      };
    }
    return {
      roles: groupScopesByDelegationRole(delegatorRole, scopes).map(
        (group) => ({
          role: toDelegationRole(group.role) ?? delegatorRole,
          constraints: group.scopes.map((scope) => ({ scope })),
        }),
      ),
    };
  }

  private upgradeableScopeValues(row: ManageDelegateRow): string[] {
    const held = new Set(this.grantedScopeValues(row).map(normalizeScopeKey));
    return this.scopeOptions()
      .filter((opt) => {
        if (opt.disabled) {
          return false;
        }
        const key = normalizeScopeKey(opt.value);
        const labelKey = normalizeScopeKey(opt.label);
        return !held.has(key) && !held.has(labelKey);
      })
      .map((opt) => opt.value);
  }

  private grantedScopeValues(row: ManageDelegateRow): string[] {
    const options = this.scopeOptions();
    const values: string[] = [];
    const rawScopes = rowScopes(row);
    const fromRefs = (row.constraintRefs ?? [])
      .map((ref) => ref.scope)
      .filter(Boolean);
    const catalog = options.map((opt) => opt.value);
    for (const scope of rawScopes.length ? rawScopes : fromRefs) {
      if (isBundledCompanionScope(scope, catalog)) {
        continue;
      }
      const key = normalizeScopeKey(scope);
      const option = options.find(
        (item) =>
          normalizeScopeKey(item.value) === key ||
          normalizeScopeKey(item.label) === key,
      );
      const value = option?.value || scope;
      if (option?.disabled || !value) {
        continue;
      }
      if (
        !values.some(
          (item) => normalizeScopeKey(item) === normalizeScopeKey(value),
        )
      ) {
        values.push(value);
      }
    }
    return values;
  }

  private applySingleAddScope(): void {
    const only = this.singleAddScope;
    if (!only || only.disabled) {
      return;
    }
    this.selectedAddScopes = [only.value];
    this.addForm.scope = only.value;
  }

  private dropGrantedAddScopes(): void {
    const existing = this.findActiveDelegateByEmail(this.addForm.email);
    if (!existing) {
      return;
    }
    const held = new Set(
      this.grantedScopeValues(existing).map(normalizeScopeKey),
    );
    const next = this.selectedAddScopes.filter(
      (scope) => !held.has(normalizeScopeKey(scope)),
    );
    if (next.length === this.selectedAddScopes.length) {
      return;
    }
    this.selectedAddScopes = next;
    this.addForm.scope = next[0] ?? '';
    this.clearAddFieldError('scope');
  }

  private async resolveDelegatorRole(): Promise<DelegationRole | null> {
    await this.rolesApi.getRoles();
    const userRoles = this.rolesApi.getUserRoles();
    const fromHighest = toDelegationRole(
      this.rolesApi.getHighestRole(userRoles),
    );
    if (fromHighest) {
      return fromHighest;
    }
    for (const role of userRoles) {
      const mapped = toDelegationRole(role);
      if (mapped) {
        return mapped;
      }
    }
    return toDelegationRole(this.tabStorageScope());
  }

  /**
   * Picker dates are IST calendar days and become end-of-day UTC.
   * A timestamp already returned by the API is UTC and is sent unchanged.
   */
  private toExpiryDateTime(value: string): string {
    const trimmed = (value ?? '').trim();
    if (/^\d{4}-\d{2}-\d{2}T/.test(trimmed)) {
      return trimmed;
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      return istToUtc(trimmed, '23:59:59');
    }
    const parsed = this.parseLooseDate(trimmed);
    if (!parsed) {
      return trimmed;
    }
    return istToUtc(this.toIsoDate(parsed), '23:59:59');
  }

  private isAllStatusesSelected(): boolean {
    const concrete = this.statusOptions.filter((o) => o.value !== 'all');
    return (
      this.selectedStatuses.length === concrete.length &&
      concrete.every((o) =>
        this.selectedStatuses.includes(
          o.value as Exclude<DelegateStatus, 'all'>,
        ),
      )
    );
  }

  private currentDateRange(): ManageDelegatesDateRange {
    return {
      startDate: this.filterDateRangeValue?.startDate
        ? this.toIsoDate(this.filterDateRangeValue.startDate)
        : null,
      endDate: this.filterDateRangeValue?.endDate
        ? this.toIsoDate(this.filterDateRangeValue.endDate)
        : null,
    };
  }

  private emitFilters(): void {
    this.filtersChange.emit({
      dateRange: this.currentDateRange(),
      statuses: [...this.selectedStatuses],
    });
    this.cdr.markForCheck();
  }

  get showPagination(): boolean {
    return this.listTotal > this.pageSize;
  }

  onSort(column: 'createdAt' | 'expiryAt'): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.reloadFromFilters();
  }

  sortIcon(column: 'createdAt' | 'expiryAt'): string {
    if (this.sortColumn !== column) return '⇅';
    return this.sortDirection === 'asc' ? '▲' : '▼';
  }

  sortAria(
    column: 'createdAt' | 'expiryAt',
  ): 'ascending' | 'descending' | 'none' {
    if (this.sortColumn !== column) return 'none';
    return this.sortDirection === 'asc' ? 'ascending' : 'descending';
  }

  onListPageChange(event: PaginatorState): void {
    const page =
      event.page != null
        ? event.page + 1
        : Math.floor((event.first ?? 0) / this.pageSize) + 1;
    if (page < 1 || page === this.currentPage) return;
    this.currentPage = page;
    this.reloadActiveList();
  }

  private reloadFromFilters(): void {
    this.currentPage = 1;
    this.reloadActiveList();
  }

  private reloadActiveList(): void {
    if (this.activeTab() === 'owners') {
      void this.loadOwners(true);
      return;
    }
    void this.loadDelegates();
  }

  private listQuery(): DelegationListQuery {
    const start = this.filterDateRangeValue?.startDate ?? null;
    const end = this.filterDateRangeValue?.endDate ?? null;
    return {
      page: this.currentPage,
      size: this.pageSize,
      ...(this.selectedStatuses.length
        ? { statuses: [...this.selectedStatuses] }
        : {}),
      ...(start && end
        ? {
            time: this.toApiDayBound(start, false),
            endtime: this.toApiDayBound(end, true),
          }
        : {}),
      ...(this.sortColumn
        ? { sort: `${this.sortColumn}:${this.sortDirection}` }
        : {}),
    };
  }

  private toIsoDate(date: Date): string {
    const y = date.getFullYear();
    const m = `${date.getMonth() + 1}`.padStart(2, '0');
    const d = `${date.getDate()}`.padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  /** Created At filter bounds: IST calendar day converted to UTC. */
  private toApiDayBound(date: Date, end: boolean): string {
    return istToUtc(this.toIsoDate(date), end ? '23:59:59' : '00:00:00');
  }

  private parseLooseDate(value: string): Date | null {
    if (!value) return null;
    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) return d;
    return null;
  }

  private isValidEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  }

  private isOwnEmail(email: string): boolean {
    const mine = this.loggedInUserEmail();
    return !!mine && mine === email.trim().toLowerCase();
  }

  private loggedInUserEmail(): string {
    try {
      const raw = localStorage.getItem('userData');
      if (!raw) {
        return '';
      }
      const parsed = JSON.parse(raw) as {
        result?: { email?: string };
        email?: string;
      };
      const email = parsed?.result?.email || parsed?.email || '';
      return String(email).trim().toLowerCase();
    } catch {
      return '';
    }
  }
}
